import React, { useMemo, useState } from "react";
import { useWeeklyContext } from "../../../../context/global/WeeklyContext";
import { useCoreContext } from "../../../../context/global/CoreContext";
import ScopeCardWrapper from "./WorkDetail/ScopeCardWrapper";
import { ConfirmModal, Button } from "../../../../components/ui";

/**
 * WorkDetailSection Component
 * Displays dispatch data and work description
 */
const WorkDetailSection = ({ data, onChange, readOnly = false }) => {
  const { scopes } = useWeeklyContext();
  const { allJobsList } = useCoreContext();
  const [selectedScopeId, setSelectedScopeId] = useState(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
  const [pendingScope, setPendingScope] = useState(null);

  // Find the selected job object
  const selectedJob = allJobsList?.find((job) => job.id === data?.job);

  // Filter scopes by job ID and date
  const filteredScopes = useMemo(() => {
    if (!scopes || !data?.job || !data?.date_performed) {
      return [];
    }

    return scopes.filter(
      (scope) => scope.job.id === data.job && scope.date === data.date_performed
    );
  }, [scopes, data?.job, data?.date_performed]);

  // Check if current description matches any scope notes
  const isDescriptionFromScope = () => {
    if (!data?.description) return false;
    return filteredScopes.some((scope) => scope.notes === data.description);
  };

  // Handle confirmed scope selection or clear action
  const confirmScopeSelection = (scope) => {
    if (scope === null) {
      // Clear action
      onChange?.({ ...data, description: "" });
      setSelectedScopeId(null);
    } else {
      // Scope selection
      setSelectedScopeId(scope.id);
      if (scope.notes && onChange) {
        onChange({ ...data, description: scope.notes });
      }
    }
    setShowOverwriteConfirm(false);
    setPendingScope(null);
  };

  // Handle scope selection - populate work description with scope notes
  const handleScopeClick = (scope) => {
    if (readOnly) return;

    // Toggle selection: if already selected, deselect it
    if (selectedScopeId === scope.id) {
      setSelectedScopeId(null);
      return;
    }

    // Check if there's existing text that doesn't match any scope
    if (data?.description && !isDescriptionFromScope()) {
      // User has custom text, show confirmation modal
      setPendingScope(scope);
      setShowOverwriteConfirm(true);
    } else {
      // No custom text or text is from a scope, proceed with selection
      confirmScopeSelection(scope);
    }
  };

  // Handle clear button - check if custom text before clearing
  const handleClear = () => {
    if (readOnly) return;

    // Check if there's existing text that doesn't match any scope
    if (data?.description && !isDescriptionFromScope()) {
      // User has custom text, show confirmation modal
      setPendingScope(null); // null indicates clear action
      setShowOverwriteConfirm(true);
    } else {
      // No custom text or text is from a scope, proceed with clearing
      onChange?.({ ...data, description: "" });
      setSelectedScopeId(null);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-700">
        Work Detail {readOnly && "(Read-only)"}
      </h2>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dispatch Data{" "}
          <i>{!readOnly && "(Click to insert work description)"}</i>
        </label>
        {!data?.job || !data?.date_performed ? (
          <div className="border rounded p-4 bg-gray-50">
            <p className="text-gray-500 text-sm">
              Select a job and date to view work scope details
            </p>
          </div>
        ) : filteredScopes.length === 0 ? (
          <div className="border rounded p-4 bg-gray-50">
            <p className="text-gray-500 text-sm">
              No work scopes found for this job and date
            </p>
          </div>
        ) : (
          <div className="border rounded p-4 bg-gray-50">
            {/* Responsive grid - automatically fits as many columns as possible (min 240px per column) */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2 gap-y-1">
              {/*
                ScopeCardWrapper is used instead of ScopeSummaryBox directly because:
                - Each grid cell can have different widths (depending on number of columns)
                - The hover tooltip needs to match the individual card width, not the full container
                - ScopeCardWrapper measures its own width using ResizeObserver
                - This ensures hover elements are correctly sized in multi-column layouts
              */}
              {filteredScopes.map((scope) => (
                <ScopeCardWrapper
                  key={scope.id}
                  scope={scope}
                  job={selectedJob}
                  isSelected={selectedScopeId === scope.id}
                  onClick={() => handleScopeClick(scope)}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-2 h-[26px]">
          <label className="block text-sm font-medium text-gray-700">
            Work Description *
          </label>
          {!readOnly && data?.description && (
            <Button
              variant="custom"
              text="Clear"
              onClick={handleClear}
              className="text-xs px-2 py-1 my-0 bg-red-50 text-red-600 border border-red-300 hover:bg-red-100"
            />
          )}
        </div>
        {readOnly ? (
          <div className="border rounded p-4 bg-gray-50">
            <p className="text-gray-700">
              {data?.description || "No description"}
            </p>
          </div>
        ) : (
          <>
            <textarea
              className="w-full focus:ring-[1px] focus:ring-gray-500 focus:outline-none border rounded p-2 caret-black"
              rows={3}
              placeholder="Describe the work performed, materials used, and any relevant details..."
              value={data?.description || ""}
              maxLength={418}
              onChange={(e) =>
                onChange?.({ ...data, description: e.target.value })
              }
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {418 - (data?.description?.length || 0)} characters remaining
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={showOverwriteConfirm}
        title={
          pendingScope === null
            ? "Clear Work Description?"
            : "Replace Work Description?"
        }
        text={
          pendingScope === null
            ? "Work description will be cleared. Are you sure you want to continue?"
            : "Work description will be replaced with the scope notes. Are you sure you want to continue?"
        }
        buttonText={pendingScope === null ? "Clear" : "Replace"}
        onConfirm={() => confirmScopeSelection(pendingScope)}
        onClose={() => {
          setShowOverwriteConfirm(false);
          setPendingScope(null);
        }}
      />
    </div>
  );
};

export default WorkDetailSection;
